import { Clock, Pencil } from 'lucide-react';
import assert from 'assert';
import { FieldType } from 'bindings/forms/FieldType';
import moment from 'moment';
import React, { ChangeEvent, useState } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { Field } from '../atoms/forms';
import { AUsers } from '../atoms/global/users';
import Dropdown from '../components_v2/dropdown/Dropdown';
import DropdownOwners from '../components_v2/dropdown/DropdownOwners';
import { DropdownStyle } from '../components_v2/dropdown/model/Model';
import Input from '../components_v2/input/Input';
import { InputStyle } from '../components_v2/input/model/Model';
import { ButtonStyle } from '../components_v2/popup/PopupCreation';
import { DayPickerModal } from '../components_v2/toolbarFilter/time/DatePicker';
import { AlertRes } from '../containers_v2/alert/AlertProvider';
import useAlert from '../containers_v2/alert/UseAlert';
import { DateAccessor } from '../containers_v2/client-companies/data/CompanyColumns';
import { creationPopupInputStyle } from '../components_v2/popup/style/CreationFormStyle';
import { FlexDiv } from '../containers_v2/products/style';
import { BorderColor, DarkGreySidely2 } from '../styles/global/css/Utils';
import { translateToNode, translateToString } from '../styles/global/translate';
import { YesNoCheckbox } from '../containers_v2/forms/PopupField';

interface FieldEditorProps {
	value: unknown | undefined;
	onChange?: (value: unknown | undefined) => void;
	onSave?: (value: unknown) => void;
	product?: ArrayBuffer;
	company?: ArrayBuffer;
	field: Field | undefined;
	inputStyle?: InputStyle | DropdownStyle;
	styledSelect?: boolean;
	autoFocus?: boolean;
	setUnsupportedField?: (value: boolean) => void;
	searchableMultiselect?: boolean;
	popupForBigFields?: boolean;
	boolButtonNullOption?: boolean;
	// z-index of the popups opened by popupForBigFields: alerts default to the
	// popup z-index (200), so an editor living inside another popup must raise
	// it to open in front (e.g. 999 like the promotion popup's own alerts)
	popupZIndex?: number;
	// restricts a Select's options (e.g. the values a field constraint allows
	// given other pinned dimensions); the caller owns the computation
	allowedValues?: string[];
}

function stringifyValue(field: Field, value: unknown | undefined): string | undefined {
	if (value === undefined || value === null) {
		return '';
	}
	switch (field.type) {
		case 'TextArea':
		case 'Text':
			return value as string;
		case 'Integer':
		case 'Number':
			return (value as number).toString();
		case 'Money':
			return (value as number / 100).toFixed(2);
		case 'Date':
			return value as string;
		case 'User':
			return value as string;
		// if (usersSync === undefined) {
		// 	console.warn('Stringifying user field without users loaded');
		// 	return value as string;
		// } else {
		// 	return usersSync.get(Number(value))?.name ?? (value as any).toString();
		// }
		case 'Url':
			return value as string;
		case 'Email':
			return value as string;
		case 'Phone':
			return value as string;
		case 'Select':
			return value as string;
		case 'Boolean':
			return value ? 'vrai' : 'faux'; // Todo use translate
	}
}

type ParseResult = string | boolean | number | null | string[];

function parseValue(type: 'Number' | 'User' | 'Integer' | 'Money', value: string): number
function parseValue(type: 'Boolean', value: string): boolean
function parseValue(type: 'Multiselect', value: string): string[]
function parseValue(type: 'File' | 'Image' | 'Location' | 'Dispatch' | 'CheckIn' | 'Signature' | 'V3User' | 'Geography' | 'Entity' | 'EntityList' | 'Period', value: string): null
function parseValue(type: 'Text' | 'Select' | 'Date' | 'Url' | 'Email' | 'Phone' | 'Address' | 'Company' | 'Form' | 'TextArea', value: string): string
function parseValue(type: FieldType, value: string): ParseResult
function parseValue(type: FieldType, value: string): ParseResult {
	switch (type) {
		case 'Multiselect':
			return value;
		case 'TextArea':
		case 'Text':
			return value.trim() !== '' ? value : null;
		case 'Integer':
		case 'Number':
			return value.trim() !== '' ? parseFloat(value) : null;
		case 'Money':
			return value.trim() !== '' ? Math.round(parseFloat(value) * 100) : null;
		case 'Date':
			return value;
		case 'User':
			return value;
		case 'Url':
			return (new URL(value)).toString();
		case 'Email':
			assert(validateEmail(value), 'Invalid email');
			return value;
		case 'Phone':
			assert(validatePhone(value), 'Invalid phone');
			return value;
		case 'Select':
			return value.trim() !== '' ? value : null;
	}
	return null;
}

function validateEmail(email: string): boolean {
	return Boolean(String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		));
}

function validatePhone(phone: string): boolean {
	return Boolean(String(phone).match(/^(\+?\d{1,3}[- ]?)?(\d{3}[- ]?){2}\d{3}$/));
}

const Select = styled.select<{ border?: boolean }>`
    ${({ border }) => border ? `height: 50px; border: 1px solid ${BorderColor}; border-radius: 5px;` : ' border: none; width: 100%;'}
	font-size: 12px;
	font-weight: 400;
	color: ${DarkGreySidely2};
`;

export default function StyledFieldEditor(props: FieldEditorProps) {
	return <RawFieldEditor inputStyle={creationPopupInputStyle} styledSelect {...props} />;
}

export function RawFieldEditor({ field, value, onChange, onSave, styledSelect, inputStyle, autoFocus, setUnsupportedField, searchableMultiselect, popupForBigFields, popupZIndex, allowedValues }: FieldEditorProps) {
	const [changed, setChanged] = useState(false);
	const owners = useRecoilValue(AUsers);
	const alert = useAlert();

	// if (field === undefined) {
	// 	return <div className="center">
	// 		<p>Unknown field</p>
	// 	</div>;
	// }

	function handleChanged(e: ChangeEvent<HTMLInputElement>) {
		setChanged(true);
		// onChange?.(e.currentTarget.value);
		if (onChange) {
			try {
				onChange(parseValue(field!.type, e.currentTarget.value));
			} catch {
				try {
					onChange(parseValue(field!.type, e.toString()));
				}
				catch {
					// TODO: Show red border
				}
			}
		}
	}

	function handleBlur(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		if (changed) {
			try {
				const v = parseValue(field!.type, e.currentTarget.value as string);
				setChanged(false);
				save(v);
			} catch (e: unknown) {
				console.error('Failed to parse: ', e);
			}
		}
	}

	function save(v: ParseResult) {
		try {
			onSave?.(v);
		} catch {
			// TODO: Show red border
		}
	}

	switch (field?.type) {
		case undefined:
			return <div className="center">
				<p>Unknown field</p>
			</div>;
		case 'Date':
			if (typeof value !== 'string' && value !== undefined && value !== null) return <></>;
			if (popupForBigFields) {
				return <FlexDiv justify='center' gap='4px' cursor='pointer' onClick={() => {
					let dateValue: moment.Moment | null = null;
					alert({
						zIndex: popupZIndex,
						title: value ? translateToString('assortments.edit_date') : translateToString('assortments.create_date'),
						content: <FlexDiv justify='center' width='400px'>
							<DayPickerModal
								style={{ width: '100%' }}
								noAbsolute
								future
								time={true}
								date={value ? moment(value) : moment(new Date())}
								buttonsBottom={true}
								onDateChange={date => {
									dateValue = date;
								}}
							/>
						</FlexDiv>,
						buttons: [
							...value ? [{
								title: translateToNode('delete'),
								res: AlertRes.Ok,
								onSubmit: () => {
									dateValue = null;
								},
								style: ButtonStyle.Error
							}] : [],
							{
								title: translateToNode('save'),
								res: AlertRes.Ok
							}
						],
						buttonMargin: '0'
					}).then(res => {
						if (res.res === AlertRes.Ok) {
							save(dateValue?.toISOString() ?? '');
						}
					});
				}}>
					<Clock color={DarkGreySidely2} size={14} />
					<div>{value ? moment(value).format('L LT') : '-'}</div>
				</FlexDiv>;
			} else {
				return <FlexDiv justify='center' width='400px'>
					<DateAccessor input inputStyle={inputStyle} date={value} pickerProps={{ buttonsBottom: true, style: { width: '100%' } }} onDateChange={date => {
						const newDate = date.toISOString();
						onChange?.(newDate);
						save(newDate);
					}} />
				</FlexDiv>;
			}
		case 'TextArea':
			if (typeof value !== 'string' && value !== undefined && value !== null) return <></>;
			if (popupForBigFields) {
				return <FlexDiv justify='center' gap='4px' cursor='pointer' height='20px' width='100%' onClick={() => {
					let textValue: string | null = null;
					alert({
						zIndex: popupZIndex,
						title: value ? translateToString('assortments.edit_textarea') : translateToString('assortments.create_textarea'),
						content: <FlexDiv justify='center' width='400px' height='30vh'>
							<Input
								inputStyle={inputStyle ? { ...inputStyle, height: 'calc(30vh - 10px)', width: '400px' } : undefined}
								name={`INPUT_FIELD[${field.id}]`}
								type='text'
								textArea={true}
								value={stringifyValue(field, value) ?? ''}
								onChange={(value) => {
									textValue = value;
								}}
								autoFocus={autoFocus} />
						</FlexDiv>,
						buttons: [
							...value ? [{
								title: translateToNode('delete'),
								res: AlertRes.Ok,
								onSubmit: () => {
									textValue = null;
								},
								style: ButtonStyle.Error
							}] : [],
							{
								title: translateToNode('save'),
								res: AlertRes.Ok
							}
						],
						buttonMargin: '0'
					}).then(res => {
						if (res.res === AlertRes.Ok) {
							save(textValue ? textValue.trim() !== '' ? textValue : null : null);
						}
					});
				}}>
					<Pencil color={DarkGreySidely2} size={14} />
					<div style={{ width: '90%', height: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{value ?? '-'}</div>
				</FlexDiv>;
			} else {
				return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type='text' textArea={true} value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
			}
		case 'Text':
			return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type='text' value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
		case 'Number':
			return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type="number" value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
		case 'Integer':
			return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type="number" value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
		case 'Money':
			return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type="number" value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
		case 'Boolean':
			return <YesNoCheckbox nullable checked={typeof value === 'boolean' ? value : undefined} onChange={(v) => { onChange?.(v); save(v ?? ''); }} />;
		case 'User': {
			const ownerDD = React.useMemo(() => owners.map((owner) => ({
				value: owner,
				label: owner.name
			}))
			, [owners]);

			return <DropdownOwners selected={ownerDD.find(o => o.value.id == value)} onChange={(v) => { onChange?.(v?.value?.id); save(v?.value!.id); }} users={ownerDD} />;
		}
		case 'Url':
			return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type="url" value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
		case 'Email':
			return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type="email" value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
		case 'Phone':
			return <Input inputStyle={inputStyle} name={`INPUT_FIELD[${field.id}]`} type="phone" value={stringifyValue(field, value) ?? ''} onChange={handleChanged} onBlur={handleBlur} autoFocus={autoFocus} />;
		case 'Multiselect':
		{
			assert(Array.isArray(field.data), `Field ${field.name} is of type multi-select but has no data (${JSON.stringify(field.data)})`);
			const valuesDD = React.useMemo(() => (field.data as string[]).filter(v => v !== null).map((v) => ({
				value: v,
				label: v,
				checked: (value as string[] | undefined)?.includes(v)
			})), [field.data, value]);
			return <Dropdown
				name={''}
				datalist={valuesDD}
				onChange={values => { const v = values.map(e => e.value); onChange?.(v); save(v); }}
				isMulti
				searchable={searchableMultiselect ?? true}
				checkbox
				checkboxAll
				doNotDeleteOptionOnSelect
				dropdownStyle={inputStyle as DropdownStyle}
				validateButton
			/>;
		}
		case 'Select':
		{
			assert(Array.isArray(field.data), `Field ${field.name} is of type select but has no data (${JSON.stringify(field.data)})`);
			const choices = (field.data as string[]).filter(v => v !== null && (!allowedValues || allowedValues.includes(v)));
			// In a grid cell the native select is what is wanted: it is compact and
			// it opens over the row. In a creation form it is the one widget that
			// does not look like the application around it — a multiselect two
			// lines above already renders as a Dropdown, and the same field on a
			// company does too.
			if (styledSelect) {
				const options = choices.map(v => ({ label: v, value: v }));
				const current = stringifyValue(field, value);
				return <Dropdown
					name={`field[${field.id}]`}
					datalist={options}
					selectedValue={options.find(option => option.value === current)}
					dropdownStyle={inputStyle as DropdownStyle}
					onChange={option => { onChange?.(option.value); save(option.value ?? null); }}
					readOnly
					cancellable
					autoOptionUp
				/>;
			}
			return <Select border={styledSelect} value={stringifyValue(field, value) ?? ''} onChange={(e) => { onChange?.(e.target.value); save(e.target.value); }} autoFocus={autoFocus}>
				<option value=''></option>
				{choices.map((v) => (
					<option value={v} key={v}>{v}</option>
				))}
			</Select>;
		}
		default:
			setUnsupportedField?.(true);
			return <div className="center">
				<p>Unsupported field type {field?.type}</p>
			</div>;

	}
}
